import { Component, HostBinding, Input } from '@angular/core';

/**
 * Shows a value on one line and cuts it with an ellipsis when it is too wide. `keepFrom` moves the cut to the middle so
 * the end, such as the port of an address, stays readable. The full value is its title.
 */
@Component({
    selector: 'app-middle-truncate',
    standalone: true,
    template: `
        <span class="fd-truncate__head">{{ head }}</span>
        @if (tail) {
        <span class="fd-truncate__tail">{{ tail }}</span>
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                min-width: 0;
                overflow: hidden;
                white-space: nowrap;
            }

            .fd-truncate__head {
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .fd-truncate__tail {
                flex: 0 0 auto;
            }
        `,
    ],
})
export class MiddleTruncateComponent {
    @Input() value = '';
    /**
     * Keeps the value from the last `keepFrom` on always in view, such as ':' for the port of an address. Empty cuts
     * the value at its end.
     */
    @Input() keepFrom = '';

    /** The full value, on hover, for a value cut by the ellipsis. */
    @HostBinding('attr.title') get title(): string {
        return this.value;
    }

    /** The part of the value that gives way to the ellipsis. */
    get head(): string {
        return this.value.slice(0, this.splitAt);
    }

    /** The part kept in view, or '' when nothing is kept. */
    get tail(): string {
        return this.value.slice(this.splitAt);
    }

    /** Where the kept part starts. A delimiter that is absent, or that opens the value, keeps nothing. */
    private get splitAt(): number {
        const index = this.keepFrom ? this.value.lastIndexOf(this.keepFrom) : -1;
        return index > 0 ? index : this.value.length;
    }
}
